(ns ycweb.app.website.cart.pages.payment.eft
  (:require
   [rum.core :as rum]
   [ycweb.app.website.cart.icons :as icons]
   [ycweb.app.website.cart.parts.common :as cart.common]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.components.feedback :as feedback]
   [ycweb.util.core :as u]
   [ycweb.util.urlstate :as urlstate]))

;; Reusable components

(rum/defc banking-details-section [order-number]
  [:div {:class "banking-details__content"}
   [:div {:class "u-max-width-64 u-margin-bottom-16"}
    (icons/fnb-bank)]

   [:h3 {:class "yc-heading yc-heading--tiny"} "First National Bank"]
   [:ul {:class "yc-list"}
    [:li "Account name: Yuppiechef Online"]
    [:li "Account number: 624-6821-4467"]
    [:li "Branch code: 201109"]
    [:li "Account type: Cheque / Current"]
    [:li "Swift code: FIRNZAJJXXX"]]

   [:p {:class "yc-paragraph yc-paragraph--large"}
    [:b (str "Payment reference: " order-number)]]])

(rum/defc payment-instructions-text [order-number]
  [:p {:class "yc-paragraph"}
   "Please email your bank-generated proof of payment to "
   [:a {:href "mailto:payments@yuppiechef.com" :class "text-link"} "payments@yuppiechef.com"]
   " We will only process your order once your payment reflects in our FNB bank account. Please use "
   order-number
   " as your payment reference — and be sure to double-check it's entered correctly to avoid delays."])

(rum/defc payment-details-list [order-number amount-owing]
  [:ul {:class "yc-list"}
   (when amount-owing
     [:li [:b (str "Amount owing: R" (u/currency-format amount-owing))]])
   [:li [:b (str "Payment reference: " order-number)]]
   [:li "Email proof of payment to: "
    [:a {:href "mailto:payments@yuppiechef.com" :class "text-link js-prevent-default"} "payments@yuppiechef.com"]]
   [:li "Once we've received your proof of payment by email, we'll send you a confirmation email letting you know that we have it and will begin to process your order."]])

;; EFT Step 01: Initial payment instructions
(rum/defc eft-step-01-to-pay < rum/reactive cmn/cursors [app-atom process-atom cart-atom]
  (let [order (cmn/curact cart-atom [:order])
        amount-owing (get-in (rum/react cart-atom) [:totals :balance])

        mark-as-paid
        (fn [e]
          (.preventDefault e)
          (swap! process-atom assoc :step :order/confirmation)
          (swap! app-atom assoc-in [:order-summary :payment-confirmation] :eft-paid))

        pay-later
        (fn [e]
          (.preventDefault e)
          (swap! process-atom assoc :step :order/confirmation)
          (swap! app-atom assoc-in [:order-summary :payment-confirmation] :eft-pay-later))]

    (cart.common/checkout-layout process-atom false
      (list
        (rum/with-key
          (feedback/feedback :info
            (str "Amount owing: R" (u/currency-format amount-owing))
            (str "Your order number is " (:number order)))
          "eft-feedback")
        [:h2 {:key "eft-heading" :class "yc-heading yc-heading--default"} "Bank details for EFT payments"]
        (rum/with-key (banking-details-section (:number order)) "banking-details")
        (rum/with-key (payment-instructions-text (:number order)) "payment-instructions")
        [:p {:key "payment-method-link" :class "yc-paragraph yc-paragraph--flush"}
         "Would you like to choose a "
         [:a.text-link
          (urlstate/href-props app-atom "order" "ShowCart"
            {:orderid (:id order) :h (:h order) :step "payment/method-postcheckout" :method "Transfer"})
          "different payment method?"]])

      (cart.common/checkout-cta-sidebar-simple
        (buttons/button-web "I have paid"
          {:on-click mark-as-paid
           :style :primary
           :class "checkout-cta__button"})
        (buttons/button-web "I will pay later"
          {:on-click pay-later
           :style :secondary
           :class "checkout-cta__button checkout-cta__button--secondary"})
        (:number order)
        cart-atom
        {:total-key :balance :hide-totals-mobile? true}))))

;; EFT Step 02a: User has paid
(rum/defc eft-step-02a-has-paid < rum/reactive cmn/cursors [app-atom process-atom cart-atom]
  (let [order-number (cmn/curact cart-atom [:order :number])]

    (cart.common/checkout-layout process-atom false
      (list
        (rum/with-key
          (feedback/feedback :success
            "We have your order"
            "Thank you. We will process it once you have paid and we receive your proof of payment. Details below.")
          "eft-feedback")
        (rum/with-key (payment-details-list order-number nil) "payment-details"))

      (cart.common/checkout-cta-sidebar-simple
        (buttons/button-web "Continue shopping"
          {:on-click
           (fn [e]
             (.preventDefault e)
             (urlstate/redirect! "/"))
           :style :cta
           :class "checkout-cta__button"})
        nil
        order-number
        cart-atom
        {:total-key :total :hide-totals-mobile? true}))))

;; EFT Step 02b: User will pay later
(rum/defc eft-step-02b-pay-later < rum/reactive cmn/cursors [app-atom process-atom cart-atom]
  (let [order-number (cmn/curact cart-atom [:order :number])
        amount-owing (cmn/curact  cart-atom [:totals :total])]

    (cart.common/checkout-layout process-atom false

      (list
        (rum/with-key
          (feedback/feedback :success
            "You have chosen to pay later for your order"
            "Thank you. We have your order and will process it once you have paid and we receive your proof of payment. Details below.")
          "eft-feedback")
        (rum/with-key (payment-details-list order-number amount-owing) "payment-details")
        [:p {:key "eft-help-link" :class "yc-paragraph"}
         "P.S. We have sent you bank details on your order confirmation email, else you can find them in "
         [:a {:href "/help.htm?action=article&id=60&name=Bank-Details-for-EFT-Payments" :class "text-link js-prevent-default"} "our Help Center"]
         "."])

      (cart.common/checkout-cta-sidebar-simple
        (buttons/button-web "Continue shopping"
          {:on-click #(urlstate/redirect app-atom nil)
           :style :cta
           :class "checkout-cta__button"})
        nil
        order-number
        cart-atom
        {:total-key :balance :hide-totals-mobile? true}))))

;; Step dispatch functions

(rum/defc eft-page < rum/reactive cmn/cursors [app-atom process-atom cart-atom]
  (let [eft-substep (cart.common/current-substep process-atom)]
    (case eft-substep
      :has-paid (eft-step-02a-has-paid app-atom process-atom cart-atom)
      :pay-later (eft-step-02b-pay-later app-atom process-atom cart-atom)
      ;; Default to initial step
      (eft-step-01-to-pay app-atom process-atom cart-atom))))